import React, { Component } from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import IntroScreen from './Components/IntroScreen';
import DowngradeSelectionScreen from './Components/DowngradeSelectionScreen';
import UpgradePreviewScreen from './Components/UpgradePreviewScreen';
import SummaryScreen from './Components/SummaryScreen';
import AmendmentProgressDialog from '../shared/Components/AmendmentProgressDialog';
import {
  extractMembersFromPolicy,
  getCoverageTypeDisplay,
  getAmendmentDirection,
  canBecomePrincipal,
  calculateMemberTotalPremium
} from './utils/coverageHelpers';
import {
  recalculateBenefitsForNewPrincipal,
  recalculatePrincipalBenefits,
  buildCoverageChangeQuotation
} from './utils/premiumCalculator';
import { processCoverageChangeAmendment } from './utils/amendmentApi';
import { getAllProducts } from '../../../../../lib/requests.es6';

class ChangeCoverageScreen extends Component {
  constructor(props) {
    super(props);

    const { policy } = props;
    const allMembers = extractMembersFromPolicy(policy);
    const currentCoverageType = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.cover_type
      : 'Member & Family';

    // Find current principal
    const principal = allMembers.find(m => m.relationship === 'Principal');
    const otherMembers = allMembers.filter(m => m.relationship !== 'Principal');

    // Store original total premium before any recalculation
    const originalTotalPremium = allMembers.reduce((sum, m) => sum + (m.premium_amount_in_cents || 0), 0);

    this.state = {
      currentCoverageType: getCoverageTypeDisplay(currentCoverageType),
      targetCoverageType: null,
      direction: null, // 'upgrade' or 'downgrade'

      allMembers,
      originalPrincipal: principal ? { ...principal } : null,
      originalTotalPremium, // Store original total
      selectedPrincipal: principal,
      removedMembers: otherMembers,

      products: props.products || [],
      currentPlan: null,
      loadingProducts: false,

      showIntro: false,
      showMainScreen: false,
      showSummary: false,

      // Amendment progress tracking
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    };
  }

  componentDidMount() {
    const { products } = this.props;

    // Fetch products if not available
    if (!products || products.length === 0) {
      this.fetchProducts();
    } else {
      this.loadCurrentPlan();
    }

    // Auto-detect target coverage type and show intro
    this.detectTargetCoverage();
  }

  fetchProducts = async () => {
    this.setState({ loadingProducts: true });

    try {
      const response = await getAllProducts();
      const products = response.data || [];

      this.setState({
        products,
        loadingProducts: false
      }, () => {
        this.loadCurrentPlan();
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
      this.setState({
        loadingProducts: false,
        error: 'Failed to load plan information. Please try again.'
      });
    }
  };

  loadCurrentPlan = () => {
    const { policy } = this.props;
    const { products } = this.state;

    if (!products || products.length === 0) return;

    const primaryPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.primary_plan
      : null;

    if (primaryPlanCode) {
      const currentPlan = products.find(p => p.code === primaryPlanCode);
      this.setState({ currentPlan });
    }
  };

  detectTargetCoverage = () => {
    const { currentCoverageType } = this.state;

    // Determine target based on current
    const targetType = currentCoverageType === 'Member & Family'
      ? 'Member Only'
      : 'Member & Family';

    const direction = getAmendmentDirection(currentCoverageType, targetType);

    this.setState({
      targetCoverageType: targetType,
      direction,
      showIntro: true
    });
  };

  // Handle proceed from intro screen
  handleProceedFromIntro = () => {
    const { selectedPrincipal, currentPlan, targetCoverageType, allMembers } = this.state;
    const { policy } = this.props;
    const oldQuotation = policy.quotation; // For amendment tracking

    // Recalculate principal benefits for both upgrade and downgrade
    if (selectedPrincipal && currentPlan && targetCoverageType) {
      const updatedPrincipal = recalculatePrincipalBenefits(
        selectedPrincipal,
        currentPlan,
        targetCoverageType,
        oldQuotation
      );

      // Update allMembers array with the recalculated principal
      const updatedAllMembers = allMembers.map(m =>
        m.uuid === updatedPrincipal.uuid ? updatedPrincipal : m
      );

      this.setState({
        selectedPrincipal: updatedPrincipal,
        allMembers: updatedAllMembers,
        showIntro: false,
        showMainScreen: true
      });
    } else {
      this.setState({
        showIntro: false,
        showMainScreen: true
      });
    }
  };

  // Handle back from intro
  handleBackFromIntro = () => {
    if (this.props.onBack) {
      this.props.onBack();
    }
  };

  // Handle promote member to principal (downgrade flow)
  handlePromoteToPrincipal = (member) => {
    const { selectedPrincipal, removedMembers, currentPlan, originalPrincipal } = this.state;
    const { policy } = this.props;
    const oldQuotation = policy.quotation; // For amendment tracking

    if (!currentPlan) {
      console.error('Plan not loaded');
      return;
    }

    // Recalculate benefits for new Principal with amendment tracking
    const newBenefits = recalculateBenefitsForNewPrincipal(member, currentPlan, 'Member Only', oldQuotation);
    const newPremium = calculateMemberTotalPremium(newBenefits);

    const newPrincipal = {
      ...member,
      relationship: 'Principal',
      benefits: newBenefits,
      premium_amount_in_cents: newPremium,
      additional_details: {
        ...member.additional_details,
        originalRelationship: member.relationship // Store original for notes
      }
    };

    // Old principal goes back to removed list with original relationship
    const oldPrincipalRestored = {
      ...selectedPrincipal,
      relationship: selectedPrincipal === originalPrincipal
        ? originalPrincipal.relationship
        : (selectedPrincipal.additional_details && selectedPrincipal.additional_details.originalRelationship)
        || 'Spouse'
    };

    // Update state
    this.setState({
      selectedPrincipal: newPrincipal,
      removedMembers: [
        ...removedMembers.filter(m => m.uuid !== member.uuid),
        oldPrincipalRestored
      ]
    });
  };

  // Check if member can be promoted
  canPromoteCheck = (member) => {
    const { currentPlan } = this.state;
    if (!currentPlan) {
      return { valid: false, reason: 'Plan not loaded' };
    }
    return canBecomePrincipal(member, currentPlan);
  };

  // Handle next to summary
  handleNext = () => {
    this.setState({
      showMainScreen: false,
      showSummary: true
    });
  };

  // Handle back from summary
  handleBackFromSummary = () => {
    this.setState({
      showSummary: false,
      showMainScreen: true
    });
  };

  // Handle cancel
  handleCancel = () => {
    if (this.props.onBack) {
      this.props.onBack();
    }
  };

  // Handle confirm and process amendment
  handleConfirmChanges = async () => {
    const { policy } = this.props;
    const {
      currentCoverageType,
      targetCoverageType,
      selectedPrincipal,
      removedMembers,
      originalPrincipal,
      allMembers
    } = this.state;

    // Check if principal was swapped
    const isPrincipalSwapped = selectedPrincipal.uuid !== originalPrincipal.uuid;
    const oldPrincipalName = `${originalPrincipal.first_name} ${originalPrincipal.last_name}`;
    const newPrincipalName = `${selectedPrincipal.first_name} ${selectedPrincipal.last_name}`;

    // Reset step statuses
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    });

    try {
      // Build quotation payload.
      // allMembers is passed so non-principal family members are preserved
      // in the payload with premium_amount_in_cents = 0 (covered under the
      // Principal's flat family rate). Without this, the backend sums each
      // member's premium individually and inflates the total.
      const quotationPayload = buildCoverageChangeQuotation(
        policy,
        selectedPrincipal,
        targetCoverageType,
        allMembers
      );

      // Process amendment with step tracking
      const updateStatus = (step, status) => {
        // Update step status (loading, success, error)
        this.setState(prevState => ({
          steps: { ...prevState.steps, [step]: status }
        }));
      };

      const result = await processCoverageChangeAmendment(
        quotationPayload,
        policy,
        currentCoverageType,
        targetCoverageType,
        removedMembers,
        newPrincipalName,
        oldPrincipalName,
        isPrincipalSwapped,
        updateStatus
      );

      // Amendment succeeded (even if note failed)
      this.setState({
        showSuccessDialog: true,
        noteError: result.noteError || null
      });
    } catch (error) {
      console.error('Failed to process coverage change:', error);
      const errorMessage = error.message || 'Failed to process coverage change. Please try again.';

      // Determine which step failed
      this.setState(prevState => {
        const quoteError = prevState.steps.quote === 'error' ? errorMessage : null;
        const swapError = prevState.steps.swap === 'error' ? errorMessage : null;

        return { quoteError, swapError };
      });
    }
  };

  // Handle success
  handleSuccess = () => {
    const { policy } = this.props;
    this.setState({ showSuccessDialog: false });
    // Navigate to policy processing page
    hashHistory.push(`admin/policy-processing/${policy.policy_number}`);
  };

  // Handle retry after error
  handleRetry = () => {
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null
    });
  };

  render() {
    const {
      currentCoverageType,
      targetCoverageType,
      direction,
      selectedPrincipal,
      removedMembers,
      originalPrincipal,
      originalTotalPremium,
      showIntro,
      showMainScreen,
      showSummary,
      steps,
      quoteError,
      swapError,
      noteError
    } = this.state;

    const { policy } = this.props;

    if (!selectedPrincipal) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Error: No Principal member found on this policy.</p>
        </div>
      );
    }

    // Calculate old and new premiums.
    // The Principal's premium_amount_in_cents always holds the correct flat rate
    // for the target coverage tier (set by recalculatePrincipalBenefits).
    // Do NOT sum across allMembers — non-principal members carry their original
    // values in state even though they are billed at 0, which would inflate the
    // displayed figure on the summary and preview screens.
    const oldPremium = originalTotalPremium;
    const newPremium = selectedPrincipal.premium_amount_in_cents;

    // Check if principal was swapped
    const isPrincipalSwapped = selectedPrincipal.uuid !== originalPrincipal.uuid;
    const oldPrincipalName = `${originalPrincipal.first_name} ${originalPrincipal.last_name}`;

    // Show intro screen
    if (showIntro) {
      return (
        <IntroScreen
          direction={direction}
          currentType={currentCoverageType}
          targetType={targetCoverageType}
          onBack={this.handleBackFromIntro}
          onProceed={this.handleProceedFromIntro}
        />
      );
    }

    // Get current plan name
    const currentPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.primary_plan
      : null;
    const currentPlanName = currentPlanCode || 'Unknown Plan';

    // Show main selection/preview screen
    if (showMainScreen) {
      if (direction === 'downgrade') {
        return (
          <DowngradeSelectionScreen
            selectedPrincipal={selectedPrincipal}
            removedMembers={removedMembers}
            currentCoverageType={currentCoverageType}
            targetCoverageType={targetCoverageType}
            currentPlan={currentPlanName}
            originalTotalPremium={originalTotalPremium}
            policy={policy}
            canPromoteCheck={this.canPromoteCheck}
            onPromoteToPrincipal={this.handlePromoteToPrincipal}
            onNext={this.handleNext}
            onCancel={this.handleCancel}
          />
        );
      } else {
        // Upgrade flow — use the Principal's flat-rate premium directly.
        // Non-principal members carry their original premium values in state
        // but are billed at 0, so summing allMembers would inflate the figure.
        return (
          <UpgradePreviewScreen
            principal={selectedPrincipal}
            allMembers={this.state.allMembers}
            oldPremium={originalTotalPremium}
            newPremium={newPremium}
            policy={policy}
            onNext={this.handleNext}
            onCancel={this.handleCancel}
          />
        );
      }
    }

    // Show summary screen
    if (showSummary) {
      return (
        <div>
          <SummaryScreen
            policy={policy}
            currentCoverageType={currentCoverageType}
            targetCoverageType={targetCoverageType}
            finalPrincipal={selectedPrincipal}
            removedMembers={direction === 'downgrade' ? removedMembers : []}
            isPrincipalSwapped={isPrincipalSwapped}
            oldPrincipalName={oldPrincipalName}
            oldPremium={oldPremium}
            newPremium={newPremium}
            onBack={this.handleBackFromSummary}
            onConfirm={this.handleConfirmChanges}
          />

          <AmendmentProgressDialog
            steps={steps}
            quoteError={quoteError}
            swapError={swapError}
            noteError={noteError}
            successMessage="Coverage type has been successfully updated. The policy is now being processed."
            onViewPolicy={this.handleSuccess}
            onRetry={this.handleRetry}
          />
        </div>
      );
    }

    return null;
  }
}

// Connect to Redux to access products
const mapStateToProps = (state) => ({
  products: state.PolicyAdmin && state.PolicyAdmin.allProducts
    ? state.PolicyAdmin.allProducts
    : []
});

export default connect(mapStateToProps)(ChangeCoverageScreen);
